<template>
  <div class="d-none" />
</template>

<script>
import { Computed, ObjectLiteral } from '@/_typedefs'
import { createNamespacedHelpers } from 'vuex'
import { groupBy, isEmpty, isNil } from 'lodash'
import { TREATMENT_EXHIBITION_RULES } from '@/store/attendance/config/treatmentExhibitionConfig'
const { mapGetters: newAttendanceFormGetters } = createNamespacedHelpers('newAttendanceForm')

export default {
  name: 'IndicatedTreatmentsNode',
  props: {
    coverageFilter: {
      type: Array,
      default: null,
    },
    showAll: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
    },
  },
  data() {
    return {
      rulesInput: {
        patientData: {},
        answers: {},
        parameters: {},
      },
      /** @type {Function?} */
      unwatch: null,
    }
  },
  computed: {
    .../** @type {{disease: Computed<ObjectLiteral>, treatments: Computed<Record<number, ObjectLiteral>>}} */ (
      newAttendanceFormGetters(['disease', 'treatments'])
    ),
    /** @type {Computed<ObjectLiteral[]>} */
    filteredTreatmentsByRules() {
      if (isEmpty(this.treatments)) {
        return []
      }
      return this.treatments.filter((treatment) => {
        const exibitionRules = treatment.exhibitionConfig

        if (!exibitionRules) {
          return true
        }

        const inclusionRules = exibitionRules.inclusion?.filter((rule) => !isNil(rule)) ?? []
        const inclusionRulesResults = inclusionRules.map(
          (rule) => TREATMENT_EXHIBITION_RULES[this.disease.id]?.inclusion[rule]?.check(this.rulesInput) ?? true,
        )

        const exclusionRules = exibitionRules.exclusion?.filter((rule) => !isNil(rule)) ?? []
        const exclusionRulesResults = exclusionRules.map(
          (rule) => TREATMENT_EXHIBITION_RULES[this.disease.id]?.exclusion[rule]?.check(this.rulesInput) ?? false,
        )

        if (inclusionRulesResults.every((rule) => !!rule) && !exclusionRulesResults.some((rule) => !!rule)) {
          return true
        }

        return false
      })
    },
    /** @type {Computed<ObjectLiteral[]>} */
    filteredTreatmentsByDosage() {
      return this.filteredTreatmentsByRules.reduce((acc, treatment) => {
        const filteredDosages = treatment.dosages.filter(
          (dosage) =>
            (dosage.minAge || 0) <= this.rulesInput.patientData.age &&
            (dosage.maxAge || Infinity) >= this.rulesInput.patientData.age &&
            (dosage.minWeight || 0) <= this.rulesInput.patientData.weight &&
            (dosage.maxWeight || Infinity) >= this.rulesInput.patientData.weight,
        )

        if (!filteredDosages.length) {
          return acc
        }

        return [
          ...acc,
          {
            ...treatment,
            dosages: filteredDosages,
          },
        ]
      }, [])
    },
    /** @type {Computed<ObjectLiteral[]>} */
    filteredTreatmentsByCoverage() {
      const filterMap = {
        public: (treatment) => treatment.availablePublicMedicalCare,
        private: (treatment) => treatment.availablePrivateMedicalCare,
      }

      const treatments = this.showAll ? this.treatments : this.filteredTreatmentsByDosage

      if (!this.coverageFilter?.length) {
        return treatments
      }

      return this.coverageFilter.reduce((acc, filter) => {
        const filterFn = filterMap[filter]
        if (!filterFn) {
          return acc
        }

        return acc.concat(treatments.filter(filterFn))
      }, [])
    },
    /** @type {Computed<ObjectLiteral>} */
    treatmentsByCategory() {
      return groupBy(this.filteredTreatmentsByCoverage, (item) => item.medicine.category.name)
    },
  },
  watch: {
    treatmentsByCategory: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.rulesInput = {
      patientData: this.$store.getters['newAttendanceForm/patientData'],
      answers: this.$store.getters['newAttendanceForm/answers'],
      parameters: this.$store.getters['newAttendanceForm/parameters'],
    }

    this.unwatch = this.$store.subscribe((mutation) => {
      if (
        ![
          'newAttendanceForm/updateAnswer',
          'newAttendanceForm/updateParameter',
          'newAttendanceForm/updatePatientData',
        ].includes(mutation.type)
      ) {
        return
      }

      this.rulesInput = {
        patientData: this.$store.getters['newAttendanceForm/patientData'],
        answers: this.$store.getters['newAttendanceForm/answers'],
        parameters: this.$store.getters['newAttendanceForm/parameters'],
      }
    })
  },
  beforeDestroy() {
    this.unwatch?.()
  },
}
</script>

<style></style>
